
import { MessageSquare, Shield, UserCheck, Clock } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsUsers } from '@/hooks/use-analytics-users';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

function formatDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PainelAnalyticsUsersPage() {
  const pageText = usePainelText('analytics_usuarios');
  const { data, loading } = useAnalyticsUsers();

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Equipe</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Administradores"
          value={loading ? '—' : formatInt(data.adminCount)}
          icon={Shield}
          variant="sky"
        />
        <PainelStatCard
          title="Editores ativos"
          value={loading ? '—' : formatInt(data.activeEditors)}
          icon={UserCheck}
          description="Login nos últimos 30 dias"
          variant="cyan"
        />
        <PainelStatCard
          title="Último login"
          value={formatDate(data.lastLoginAt)}
          icon={Clock}
          variant="indigo"
        />
        <PainelStatCard
          title="Comentários internos"
          value={loading ? '—' : formatInt(data.internalComments)}
          icon={MessageSquare}
          description="Reservado para integração futura"
          variant="violet"
        />
      </div>

      <AnalyticsPanel title="Publicações por usuário" subtitle="Atividade no CMS">
        <div className={cn(painelTableShellClass, 'overflow-hidden')}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-separate border-spacing-0">
              <thead>
                <tr className="bg-white/[0.03] font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
                  <th className="px-4 py-3 text-left">Usuário</th>
                  <th className="px-4 py-3 text-left">Função</th>
                  <th className="px-4 py-3 text-right">Publicações</th>
                  <th className="px-4 py-3 text-right">Atualizações</th>
                  <th className="px-4 py-3 text-right">Último login</th>
                </tr>
              </thead>
              <tbody className="font-inter text-sm">
                {data.users.map((row) => (
                  <tr key={String(row.userId)} className="text-blue-100/90">
                    <td className="border-t border-white/[0.06] px-4 py-3">{row.name}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-slate-400">{row.role}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.publications)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right tabular-nums">{formatInt(row.updates)}</td>
                    <td className="border-t border-white/[0.06] px-4 py-3 text-right text-slate-400">
                      {formatDate(row.lastLoginAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
